import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { AuditLog } from '../../services/auditService';
import { AttachmentDisplay } from './AttachmentDisplay';
import {
  ClockIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon as TrashIconOutline,
  ArrowPathIcon,
  PaperClipIcon
} from '@heroicons/react/24/outline';
import { ChatBubbleLeftRightIcon as ChatAltIcon } from '@heroicons/react/24/outline';
import { format, parseISO } from 'date-fns';

interface TicketHistoryProps {
  auditLogs: AuditLog[];
  isLoadingAuditLogs: boolean;
  fetchAuditLogs: () => void;
}

export default function TicketHistory({
  auditLogs,
  isLoadingAuditLogs,
  fetchAuditLogs
}: TicketHistoryProps) {
  const { t } = useTranslation();
  const { theme } = useTheme();

  return (
    <div
      className={`mt-6 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} shadow-sm rounded-xl p-6`}
    >
      <div className="flex justify-between items-center mb-4">
        <h2
          className={`text-lg font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-900'}`}
        >
          {t('pages.ticketDetail.ticketHistory')}
        </h2>
        <button
          onClick={fetchAuditLogs}
          className={`p-1.5 rounded-full ${theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
          title="Refresh history"
        >
          <ArrowPathIcon className="h-5 w-5 text-gray-500" />
        </button>
      </div>

      {isLoadingAuditLogs ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : auditLogs.length === 0 ? (
        <div
          className={`text-center py-8 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}
        >
          No history records found
        </div>
      ) : (
        <div className="relative">
          <div
            className={`absolute left-3 top-1 bottom-0 w-0.5 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'}`}
            aria-hidden="true"
          ></div>

          <div className="space-y-6">
            {auditLogs.map((log) => {
              let Icon = ClockIcon;
              let iconColor = '';

              if (log.entity_type === 'attachment') {
                Icon = PaperClipIcon;
                iconColor =
                  theme === 'dark'
                    ? 'text-amber-400 bg-amber-900/30'
                    : 'text-amber-600 bg-amber-100';
              } else if (log.entity_type === 'comment' && log.action === 'CREATE') {
                Icon = ChatAltIcon;
                iconColor =
                  theme === 'dark'
                    ? 'text-purple-400 bg-purple-900/30'
                    : 'text-purple-600 bg-purple-100';
              } else if (log.action === 'CREATE') {
                Icon = PlusIcon;
                iconColor =
                  theme === 'dark'
                    ? 'text-green-400 bg-green-900/30'
                    : 'text-green-600 bg-green-100';
              } else if (log.action === 'UPDATE') {
                Icon = PencilIcon;
                iconColor =
                  theme === 'dark'
                    ? 'text-blue-400 bg-blue-900/30'
                    : 'text-blue-600 bg-blue-100';
              } else if (log.action === 'DELETE') {
                Icon = TrashIconOutline;
                iconColor =
                  theme === 'dark'
                    ? 'text-red-400 bg-red-900/30'
                    : 'text-red-600 bg-red-100';
              }

              const formattedDate = format(parseISO(log.changed_at), 'MMM d, yyyy HH:mm');

              let changeTitle = '';
              let changeContent: string | ReactNode = '';

              if (
                log.entity_type === 'comment' &&
                log.action === 'CREATE' &&
                log.new_data
              ) {
                const commentContent = log.new_data.content;
                if (commentContent) {
                  const maxLength = 100;
                  const truncatedContent =
                    commentContent.length > maxLength
                      ? commentContent.substring(0, maxLength) + '...'
                      : commentContent;

                  changeTitle = 'Added comment';
                  changeContent = truncatedContent;
                } else {
                  changeTitle = 'Added a comment';
                  changeContent = '';
                }
              } else if (log.entity_type === 'attachment') {
                if (log.action === 'CREATE' && log.new_data) {
                  const fileName = log.new_data.file_name;
                  const fileType = log.new_data.file_type;
                  changeTitle = 'Uploaded attachment';
                  changeContent = (
                    <AttachmentDisplay fileName={fileName} fileType={fileType} />
                  );
                } else if (log.action === 'DELETE' && log.old_data) {
                  const fileName = log.old_data.file_name;
                  changeTitle = 'Deleted attachment';
                  changeContent = (
                    <AttachmentDisplay fileName={fileName} />
                  );
                }
              } else if (log.action === 'CREATE') {
                changeTitle = 'Ticket was created';
                changeContent = '';
              } else if (log.action === 'DELETE') {
                changeTitle = 'Ticket was deleted';
                changeContent = '';
              } else if (log.action === 'UPDATE' && log.new_data) {
                const changes = [];

                if (log.new_data.status !== undefined) {
                  if (
                    log.old_data &&
                    log.old_data.status !== undefined &&
                    log.old_data.status !== log.new_data.status
                  ) {
                    changes.push(
                      `Status changed from "${log.old_data.status}" to "${log.new_data.status}"`
                    );
                  } else if (!log.old_data && log.new_data.status === 'closed') {
                    changes.push(`Status changed to "closed"`);
                  }
                }

                if (
                  log.old_data &&
                  log.new_data.priority !== undefined &&
                  log.old_data.priority !== undefined &&
                  log.old_data.priority !== log.new_data.priority
                ) {
                  changes.push(
                    `Priority changed from "${log.old_data.priority}" to "${log.new_data.priority}"`
                  );
                }

                for (const key in log.new_data) {
                  if (
                    !log.old_data ||
                    JSON.stringify(log.old_data[key]) !== JSON.stringify(log.new_data[key])
                  ) {
                    if (
                      key === 'status' ||
                      key === 'priority' ||
                      key === 'updated_at' ||
                      key === 'updated_by_id'
                    ) {
                      continue;
                    }

                    if (key === 'title') {
                      changeTitle = 'Title was updated';
                    } else if (key === 'description') {
                      changeTitle = 'Description was updated';
                    } else if (key === 'assignee_id') {
                      changeTitle = 'Assignee was changed';
                    } else if (key === 'customer_id') {
                      changeTitle = 'Customer was changed';
                    } else if (key === 'group_id') {
                      changeTitle = 'Group was changed';
                    } else {
                      const fieldName = key
                        .replace(/_/g, ' ')
                        .replace(/\b\w/g, (c) => c.toUpperCase());
                      changeTitle = `${fieldName} was updated`;
                    }
                  }
                }
                changeTitle = changes.join(', ');
              }

              if ((log.action === 'UPDATE' && changeTitle === '') || changeTitle === '') {
                return null;
              }

              return (
                <div key={log.id} className="relative flex items-start">
                  <div className="relative">
                    <span
                      className={`h-7 w-7 rounded-full flex items-center justify-center ring-1 ring-opacity-25 ${iconColor}`}
                      aria-hidden="true"
                    >
                      <Icon className="h-4 w-4" />
                    </span>
                  </div>

                  <div className="ml-4 min-w-0 flex-1">
                    <div className="flex justify-between">
                      <div className="w-1/2">
                        <p
                          className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-900'}`}
                        >
                          {changeTitle}
                        </p>
                      </div>
                      <p
                        className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} whitespace-nowrap`}
                      >
                        {formattedDate}
                      </p>
                    </div>
                    <div className="">
                      {changeContent && (
                          typeof changeContent === 'string' ? (
                            <p
                              className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-900'} `}
                            >
                              {`"${changeContent}"`}
                            </p>
                          ) : (
                            <div className="mt-1">
                              {changeContent}
                            </div>
                          )
                        )}
                    </div>
                    <p
                      className={`mt-0.5 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}
                    >
                      by{' '}
                      {log.user_info && log.user_info.name ? (
                        <>
                          <span className="font-medium">{log.user_info.name}</span>
                          {log.user_info.role && (
                            <span
                              className={`ml-1.5 px-1.5 py-0.5 text-xs rounded-full ${theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}
                            >
                              {log.user_info.role}
                            </span>
                          )}
                        </>
                      ) : (
                        'System'
                      )}
                    </p>

                    {((log.action === 'UPDATE' && log.new_data) ||
                      (log.entity_type === 'comment' &&
                        log.action === 'CREATE' &&
                        log.new_data)) && (
                      <details className="mt-2">
                        <summary
                          className={`text-xs cursor-pointer ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`}
                        >
                          View details
                        </summary>
                        <div
                          className={`mt-2 p-3 rounded-md text-xs font-mono overflow-auto max-h-60 ${theme === 'dark' ? 'bg-gray-900 text-gray-300' : 'bg-gray-50 text-gray-800'}`}
                        >
                          <div className="space-y-4">
                            {log.entity_type === 'comment' &&
                              log.action === 'CREATE' &&
                              log.new_data && (
                                <div>
                                  <p className="font-semibold mb-2 text-sm">
                                    Comment Content
                                  </p>
                                  <div className="p-3 rounded-md">
                                    <p className="whitespace-pre-wrap">
                                      {log.new_data.content}
                                    </p>
                                  </div>

                                  <div className="mt-4 pt-4 border-t border-gray-700">
                                    <p className="font-semibold mb-2 text-sm">
                                      Comment Details
                                    </p>
                                    <table className="w-full border-collapse">
                                      <tbody>
                                        <tr>
                                          <td className="px-2 py-1 font-medium">
                                            Comment ID
                                          </td>
                                          <td className="px-2 py-1">{log.new_data.id}</td>
                                        </tr>
                                        <tr>
                                          <td className="px-2 py-1 font-medium">
                                            Created At
                                          </td>
                                          <td className="px-2 py-1">
                                            {new Date(
                                              log.new_data.created_at
                                            ).toLocaleString()}
                                          </td>
                                        </tr>
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              )}

                            {log.action === 'UPDATE' && log.new_data && (
                              <div>
                                <p className="font-semibold mb-2 text-sm">Changes</p>
                                <table className="w-full border-collapse">
                                  <thead>
                                    <tr>
                                      <th
                                        className={`text-left px-2 py-1 ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-100'}`}
                                      >
                                        Field
                                      </th>
                                      <th
                                        className={`text-left px-2 py-1 ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-100'}`}
                                      >
                                        Previous
                                      </th>
                                      <th
                                        className={`text-left px-2 py-1 ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-100'}`}
                                      >
                                        New
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {Object.keys(log.new_data).map((key) => {
                                      const oldValue = log.old_data?.[key];
                                      const newValue = log.new_data?.[key];
                                      const hasChanged =
                                        !log.old_data ||
                                        (log.old_data &&
                                          JSON.stringify(oldValue) !==
                                            JSON.stringify(newValue));

                                      if (
                                        !hasChanged ||
                                        (oldValue === undefined && key !== 'status')
                                      )
                                        return null;

                                      return (
                                        <tr
                                          key={key}
                                          className={`${theme === 'dark' ? 'hover:bg-gray-800' : 'hover:bg-gray-50'}`}
                                        >
                                          <td className="px-2 py-1 align-top font-medium">
                                            {key
                                              .replace(/_/g, ' ')
                                              .replace(/\b\w/g, (c) => c.toUpperCase())}
                                          </td>
                                          <td className="px-2 py-1 align-top">
                                            {typeof oldValue === 'object'
                                              ? JSON.stringify(oldValue)
                                              : String(oldValue || '')}
                                          </td>
                                          <td className="px-2 py-1 align-top">
                                            {typeof newValue === 'object'
                                              ? JSON.stringify(newValue)
                                              : String(newValue || '')}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        </div>
                      </details>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
